// ── Timezone-aware conversion helpers ────────────────────────────────────────
//
// `DateTimeInput` always exchanges absolute UTC instants (ISO strings) with its
// caller. These helpers convert between that absolute instant and the
// wall-clock string a `datetime-local` input needs, for an arbitrary IANA
// timezone (not just the device's own timezone).

/** Curated fallback list used only when the runtime lacks `Intl.supportedValuesOf`. */
const FALLBACK_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
  'Pacific/Auckland',
]

/**
 * Preferred representatives when several IANA zones share the same current UTC offset.
 * Earlier entries win so the picker stays short and readable (e.g. `Asia/Tokyo` over
 * `Asia/Seoul` for UTC+09:00; `Europe/Rome` over other CET cities).
 */
const PREFERRED_TIMEZONES = [
  'UTC',
  ...FALLBACK_TIMEZONES.filter((z) => z !== 'UTC'),
  'Europe/Rome',
  'America/Toronto',
  'America/Mexico_City',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Pacific/Honolulu',
]

/**
 * Returns the IANA timezone name detected from the current device/browser, normalized
 * to a safe, user-facing zone (see {@link normalizeTimeZone}). Falls back to `"UTC"`.
 */
export function getDeviceTimezone(): string {
  try {
    return normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC')
  } catch {
    return 'UTC'
  }
}

// ── Exotic timezone normalization ────────────────────────────────────────────
//
// Some runtimes (documented ICU/CLDR fallback bugs on certain Windows builds)
// report `Intl.DateTimeFormat().resolvedOptions().timeZone` as an exotic
// identifier — e.g. `Etc/GMT-9` instead of `Asia/Tokyo` — rather than a normal
// `Area/City` region zone. These identifiers are technically valid IANA zones
// (Intl accepts them), but they read as confusing/sign-inverted to users and
// don't appear in `listTimezones()` (which collapses `Etc/*` zones out). This
// module normalizes any such identifier to a canonical, "safe" region zone
// before it is ever used as a default or displayed.

/** True when `zone` is a clean, user-facing identifier: `"UTC"` or an `Area/City` region zone. */
export function isSafePersistableZone(zone: string): boolean {
  if (zone === 'UTC') return true
  return zone.includes('/') && !zone.startsWith('Etc/') && !/\d/.test(zone)
}

/** Case-insensitive UTC-family aliases — all mean exactly UTC+00:00. */
const UTC_FAMILY = new Set(
  [
    'UTC',
    'UCT',
    'GMT',
    'GMT0',
    'GMT+0',
    'GMT-0',
    'Greenwich',
    'Universal',
    'Zulu',
    'Z',
    'Etc/UTC',
    'Etc/UCT',
    'Etc/GMT',
    'Etc/GMT0',
    'Etc/GMT+0',
    'Etc/GMT-0',
    'Etc/Greenwich',
    'Etc/Universal',
    'Etc/Zulu',
    'Factory',
    'Etc/Unknown',
  ].map((z) => z.toLowerCase())
)

/** tzdata "backward" legacy links and common non-IANA abbreviations, resolved case-sensitively. */
const ALIAS_MAP: Record<string, string> = {
  Japan: 'Asia/Tokyo',
  'US/Eastern': 'America/New_York',
  'US/Central': 'America/Chicago',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Europe/Kiev': 'Europe/Kyiv',
  Hongkong: 'Asia/Hong_Kong',
  Singapore: 'Asia/Singapore',
  ROK: 'Asia/Seoul',
  PRC: 'Asia/Shanghai',
  // POSIX abbreviation pairs (DST-observing) and fixed-offset abbreviations.
  EST5EDT: 'America/New_York',
  CST6CDT: 'America/Chicago',
  MST7MDT: 'America/Denver',
  PST8PDT: 'America/Los_Angeles',
  EST: 'America/Panama',
  MST: 'America/Phoenix',
  HST: 'Pacific/Honolulu',
  WET: 'Europe/Lisbon',
  CET: 'Europe/Paris',
  MET: 'Europe/Paris',
  EET: 'Europe/Bucharest',
  JST: 'Asia/Tokyo',
}

/**
 * Non-DST region representative per fixed UTC offset (minutes east of UTC).
 * Used as the fallback when normalizing a raw offset (`Etc/GMT±N`, `+09:00`, ...).
 * Offsets with no safely non-DST inhabited zone fall back to `"UTC"` (e.g. `Etc/GMT+12`).
 */
const OFFSET_TO_ZONE: Record<number, string> = {
  [-600]: 'Pacific/Honolulu',
  [-540]: 'Pacific/Gambier',
  [-480]: 'Pacific/Pitcairn',
  [-420]: 'America/Phoenix',
  [-360]: 'America/Belize',
  [-300]: 'America/Panama',
  [-240]: 'America/Guyana',
  [-180]: 'America/Argentina/Buenos_Aires',
  [-120]: 'America/Noronha',
  [-60]: 'Atlantic/Cape_Verde',
  0: 'UTC',
  60: 'Africa/Lagos',
  120: 'Africa/Cairo',
  180: 'Europe/Moscow',
  240: 'Asia/Dubai',
  300: 'Asia/Karachi',
  330: 'Asia/Kolkata',
  345: 'Asia/Kathmandu',
  360: 'Asia/Dhaka',
  420: 'Asia/Bangkok',
  480: 'Asia/Shanghai',
  540: 'Asia/Tokyo',
  570: 'Australia/Darwin',
  600: 'Australia/Brisbane',
  660: 'Pacific/Noumea',
  720: 'Pacific/Auckland',
  765: 'Pacific/Chatham',
  780: 'Pacific/Tongatapu',
  840: 'Pacific/Kiritimati',
}

/** Maps a fixed UTC offset (minutes east of UTC) to a safe, non-DST region zone. Never throws. */
export function offsetMinutesToZone(minutes: number): string {
  return OFFSET_TO_ZONE[minutes] ?? 'UTC'
}

/** Matches `Etc/GMT±N` (POSIX-style, sign inverted relative to ISO 8601). */
const ETC_GMT_REGEX = /^Etc\/GMT([+-])(\d{1,2})$/i

/** Matches raw ISO/lenient offset strings: `+09:00`, `+0900`, `+09`, `UTC+9`, `GMT+9`. */
const OFFSET_STRING_REGEX = /^(?:UTC|GMT)?([+-])(\d{1,2})(?::?(\d{2}))?$/i

/**
 * Normalizes any timezone identifier — exotic IANA aliases, POSIX-style fixed
 * offsets, legacy backward links, or raw offset strings — to a "safe",
 * user-facing zone: `"UTC"` or a clean `Area/City` region zone.
 *
 * Already-clean region zones pass through untouched. Never throws — unknown
 * or malformed input falls back to `"UTC"`.
 */
export function normalizeTimeZone(raw: string | null | undefined): string {
  const zone = (raw ?? '').trim()
  if (zone === '') return 'UTC'
  if (UTC_FAMILY.has(zone.toLowerCase())) return 'UTC'
  if (ALIAS_MAP[zone]) return ALIAS_MAP[zone]
  if (isSafePersistableZone(zone)) return zone

  // Etc/GMT±N: sign is inverted relative to ISO 8601 (Etc/GMT-9 == UTC+9).
  const etcMatch = ETC_GMT_REGEX.exec(zone)
  if (etcMatch) {
    const sign = etcMatch[1] === '-' ? 1 : -1
    return offsetMinutesToZone(sign * Number(etcMatch[2]) * 60)
  }

  // Raw offset strings follow normal ISO 8601 convention — no sign inversion.
  const offsetMatch = OFFSET_STRING_REGEX.exec(zone)
  if (offsetMatch) {
    const sign = offsetMatch[1] === '-' ? -1 : 1
    const hours = Number(offsetMatch[2])
    const minutes = offsetMatch[3] ? Number(offsetMatch[3]) : 0
    return offsetMinutesToZone(sign * (hours * 60 + minutes))
  }

  // Sentinels / garbage / unrecognized zone names: safe fallback, never throws.
  return 'UTC'
}

/** Parses `"UTC+09:00"` / `"UTC-05:00"` into minutes east of UTC for sorting. */
function offsetLabelToMinutes(label: string): number {
  const match = /^UTC([+-])(\d{2}):(\d{2})$/.exec(label)
  if (!match) return 0
  const sign = match[1] === '-' ? -1 : 1
  return sign * (Number(match[2]) * 60 + Number(match[3]))
}

/** Lower rank = more preferred. Non-preferred city zones beat `Etc/*` aliases. */
function timezonePreferenceRank(zone: string): number {
  const preferredIndex = PREFERRED_TIMEZONES.indexOf(zone)
  if (preferredIndex >= 0) return preferredIndex
  if (zone.startsWith('Etc/')) return 10_000
  return 1_000
}

/** True when `candidate` should replace `current` as the representative for a shared offset. */
function isPreferredTimezone(candidate: string, current: string): boolean {
  const candidateRank = timezonePreferenceRank(candidate)
  const currentRank = timezonePreferenceRank(current)
  if (candidateRank !== currentRank) return candidateRank < currentRank
  return candidate.localeCompare(current) < 0
}

/**
 * Returns IANA timezones for the picker: one representative per current UTC offset
 * (e.g. a single `UTC+02:00` city, not Rome and Ljubljana and every other CET zone).
 * Sorted west-to-east by offset. Always includes `"UTC"`.
 */
export function listTimezones(): string[] {
  const supported =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : null
  const zones = supported && supported.length > 0 ? supported : FALLBACK_TIMEZONES
  // Not every runtime includes the "UTC" alias in supportedValuesOf('timeZone').
  const withUtc = zones.includes('UTC') ? zones : ['UTC', ...zones]

  const byOffset = new Map<string, string>()
  for (const zone of withUtc) {
    const offset = getTimezoneOffsetLabel(zone)
    if (!offset) continue
    const existing = byOffset.get(offset)
    if (!existing || isPreferredTimezone(zone, existing)) {
      byOffset.set(offset, zone)
    }
  }

  return [...byOffset.entries()]
    .sort((a, b) => offsetLabelToMinutes(a[0]) - offsetLabelToMinutes(b[0]))
    .map(([, zone]) => zone)
}

/** Formats the current UTC offset of a timezone, e.g. `"UTC+09:00"` or `"UTC-05:00"`. */
export function getTimezoneOffsetLabel(timeZone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      // 'longOffset' is zero-padded (e.g. "GMT+09:00"); 'shortOffset' would give "GMT+9".
      timeZoneName: 'longOffset',
    }).formatToParts(at)
    // Guaranteed present for a valid timeZone with timeZoneName requested — if it were ever
    // missing, `.replace` on `undefined` throws and is caught below, returning ''.
    const offset = parts.find((p) => p.type === 'timeZoneName')!.value
    return offset.replace('GMT', 'UTC')
  } catch {
    return ''
  }
}

/**
 * Well-known abbreviation for each zone this picker can show as a representative
 * (see {@link PREFERRED_TIMEZONES} / {@link FALLBACK_TIMEZONES}). Kept as a fixed lookup
 * rather than derived from `Intl` because CLDR's locale data is inconsistent about which
 * zones get a short name at all (many, including all of continental Europe, fall back to
 * a numeric `GMT+X` instead of `CEST`) — and, per product decision, these acronyms are
 * intentionally the *same string in every UI language* rather than translated, since
 * that's how they're written/recognized everywhere (nobody translates "CEST" into
 * Japanese). A zone with a `std`/`dst` pair switches between the two based on whether
 * `at` currently falls in daylight saving for that zone; a plain string is a zone with a
 * single, unchanging abbreviation.
 */
const ZONE_ABBREVIATIONS: Record<string, string | { std: string; dst: string }> = {
  UTC: 'UTC',
  'America/New_York': { std: 'EST', dst: 'EDT' },
  'America/Chicago': { std: 'CST', dst: 'CDT' },
  'America/Denver': { std: 'MST', dst: 'MDT' },
  'America/Los_Angeles': { std: 'PST', dst: 'PDT' },
  'America/Toronto': { std: 'EST', dst: 'EDT' },
  'America/Mexico_City': 'CST',
  'America/Sao_Paulo': 'BRT',
  'Europe/London': { std: 'GMT', dst: 'BST' },
  'Europe/Paris': { std: 'CET', dst: 'CEST' },
  'Europe/Berlin': { std: 'CET', dst: 'CEST' },
  'Europe/Rome': { std: 'CET', dst: 'CEST' },
  'Europe/Moscow': 'MSK',
  'Africa/Cairo': 'EET',
  'Africa/Johannesburg': 'SAST',
  'Asia/Dubai': 'GST',
  'Asia/Kolkata': 'IST',
  'Asia/Bangkok': 'ICT',
  'Asia/Shanghai': 'CST',
  'Asia/Singapore': 'SGT',
  'Asia/Hong_Kong': 'HKT',
  'Asia/Tokyo': 'JST',
  'Asia/Seoul': 'KST',
  'Australia/Sydney': { std: 'AEST', dst: 'AEDT' },
  'Pacific/Auckland': { std: 'NZST', dst: 'NZDT' },
  'Pacific/Honolulu': 'HST',
}

/** Minutes east of UTC for `zone` at `at`, or `null` if the runtime can't resolve it. */
function utcOffsetMinutes(zone: string, at: Date): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longOffset',
    }).formatToParts(at)
    const match = /^GMT([+-])(\d{2}):(\d{2})$/.exec(
      parts.find((p) => p.type === 'timeZoneName')!.value
    )
    if (!match) return 0
    const sign = match[1] === '-' ? -1 : 1
    return sign * (Number(match[2]) * 60 + Number(match[3]))
  } catch {
    return null
  }
}

/**
 * True when `zone` is currently observing daylight saving at `at`. Samples the zone's
 * offset on January 1st and July 1st of the same year — one of those two dates is
 * guaranteed to fall outside any DST window regardless of hemisphere, so the smaller
 * (less-ahead-of-UTC) of the two is the zone's standard-time offset and the larger is its
 * DST offset (clocks always move *forward* for DST, in both hemispheres). `at` is then
 * classified as DST if its own offset matches that larger value. A zone with no DST has
 * matching January/July offsets, so this always reports `false` for it.
 */
function isDaylightSavingNow(zone: string, at: Date): boolean {
  const current = utcOffsetMinutes(zone, at)
  const jan1 = utcOffsetMinutes(zone, new Date(at.getFullYear(), 0, 1))
  const jul1 = utcOffsetMinutes(zone, new Date(at.getFullYear(), 6, 1))
  if (current === null || jan1 === null || jul1 === null) return false
  const standardOffset = Math.min(jan1, jul1)
  return current > standardOffset
}

function curatedAbbreviationForZone(timeZone: string, at: Date): string | null {
  const entry = ZONE_ABBREVIATIONS[timeZone]
  if (!entry) return null
  if (typeof entry === 'string') return entry
  return isDaylightSavingNow(timeZone, at) ? entry.dst : entry.std
}

/** Picks the preferred curated zone that shares `offsetLabel` at `at` (same rules as the picker). */
function curatedAbbreviationForOffset(offsetLabel: string, at: Date): string | null {
  let representative: string | null = null
  for (const zone of Object.keys(ZONE_ABBREVIATIONS)) {
    if (getTimezoneOffsetLabel(zone, at) !== offsetLabel) continue
    if (!representative || isPreferredTimezone(zone, representative)) {
      representative = zone
    }
  }
  return representative ? curatedAbbreviationForZone(representative, at) : null
}

/**
 * Returns `zone`'s fixed, language-independent abbreviation (e.g. `"CEST"`, `"ICT"`,
 * `"JST"`) for zones with a well-known one, switching between `std`/`dst` forms as
 * appropriate for the given instant. When the zone itself has no curated entry, uses the
 * abbreviation for the same UTC offset (e.g. `Europe/Zurich` → `CEST` via `Europe/Rome`).
 * Falls back to the zone's city name only when no curated zone shares that offset.
 */
export function getTimezoneAbbreviation(timeZone: string, at: Date = new Date()): string {
  const direct = curatedAbbreviationForZone(timeZone, at)
  if (direct) return direct

  const offset = getTimezoneOffsetLabel(timeZone, at)
  if (offset) {
    const byOffset = curatedAbbreviationForOffset(offset, at)
    if (byOffset) return byOffset
  }

  return timeZone.slice(timeZone.lastIndexOf('/') + 1).replace(/_/g, ' ')
}

/**
 * User-facing timezone label: fixed abbreviation (or city fallback) plus UTC offset,
 * e.g. `"JST (UTC+09:00)"`, `"CEST (UTC+02:00)"`.
 *
 * Shared by the timezone picker, event datetime displays, audit timestamps, and the
 * editor summary line so every surface shows the same style.
 */
export function timezoneLabel(timeZone: string, at: Date = new Date()): string {
  const abbr = getTimezoneAbbreviation(timeZone, at)
  const offset = getTimezoneOffsetLabel(timeZone, at)
  return offset ? `${abbr} (${offset})` : abbr
}

/** Field options shared by both conversion directions — second-level precision, 24h clock. */
const ZONED_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
}

function formatPartsToMap(parts: Intl.DateTimeFormatPart[]): Record<string, string> {
  const map: Record<string, string> = {}
  for (const part of parts) {
    if (part.type !== 'literal') map[part.type] = part.value
  }
  return map
}

/**
 * Converts a `datetime-local` input value (wall-clock time with no timezone info,
 * e.g. `"2026-09-01T14:45"`) — interpreted as a moment in `timeZone` — into an
 * absolute UTC ISO string. Returns `''` if `localValue` is empty/invalid.
 */
export function zonedValueToUtcIso(localValue: string, timeZone: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(localValue)
  if (!match) return ''
  const [year, month, day, hour, minute] = match.slice(1).map(Number)
  // Step 1: treat the wall-clock fields as if they were already UTC (a first guess).
  const guessUtcMs = Date.UTC(year, month - 1, day, hour, minute)

  // Step 2: render that guessed instant back through `timeZone` to see how far off it is.
  const rendered = formatPartsToMap(
    new Intl.DateTimeFormat('en-US', { ...ZONED_FORMAT_OPTIONS, timeZone }).formatToParts(
      new Date(guessUtcMs)
    )
  )
  const renderedAsUtcMs = Date.UTC(
    Number(rendered.year),
    Number(rendered.month) - 1,
    Number(rendered.day),
    Number(rendered.hour),
    Number(rendered.minute),
    Number(rendered.second)
  )

  // Step 3: the difference is the timezone's offset — subtract it from the guess.
  const offsetMs = renderedAsUtcMs - guessUtcMs
  return new Date(guessUtcMs - offsetMs).toISOString()
}

/**
 * Converts an absolute UTC ISO string into a `datetime-local` input value
 * (wall-clock time, no timezone info) representing that same instant in `timeZone`.
 * Returns `''` if `isoUtc` is empty/invalid.
 */
export function utcIsoToZonedValue(isoUtc: string, timeZone: string): string {
  if (!isoUtc) return ''
  const date = new Date(isoUtc)
  if (Number.isNaN(date.getTime())) return ''
  const parts = formatPartsToMap(
    new Intl.DateTimeFormat('en-US', { ...ZONED_FORMAT_OPTIONS, timeZone }).formatToParts(date)
  )
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
}
